import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    MobileMicroserviceBox,
    ModalsProvider,
    TextButton,
} from '@speeki/global-ui-components';
import { Typography } from '@speeki/global-ui-components';

import MobileDashboardNicole from '@assets/images/mobile_nicole_dashboard.png';

import { useBackToEntitySelection } from '@utils/hooks/useBackToEntitySelection';

import { getPath } from '@services/RouteService/paths';

import { useReactiveVar } from '@apollo/client';
import {
    authVar,
    servicesStatistics,
    subsidiarySiteUuidFromUrlVar,
    tenantModulesVar,
} from '@boot/reactiveVariables';
import { SwitchToEntity } from '@domains/dashboard/organisms/MicroservicesTiles/MicroservicesTiles.styled';

import {
    MobileTilesWrapper,
    NicoleWrapper,
    SwitchToEntityMobileText,
    TilesContainer,
    TilesHeader,
    TilesHeaderAction,
    TilesSection,
} from './MobileTiles.styled';

interface MobileTilesProps {
    modules: {
        cases: boolean;
        disclosures: boolean;
        surveys: boolean;
    };
}

export const MobileTiles = ({ modules }: MobileTilesProps) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { isGuest, isLoggedIn } = useReactiveVar(authVar);
    const {
        disclosuresCount,
        ongoingCases,
        respondedDisclosures,
        surveysCount,
    } = useReactiveVar(servicesStatistics);

    const isSubsidiaryEnabled =
        useReactiveVar(tenantModulesVar).subsidiarySites;
    const subsidiarySiteUuidFromUrl = useReactiveVar(
        subsidiarySiteUuidFromUrlVar,
    );
    const isSubsidiaryChosen = isSubsidiaryEnabled && subsidiarySiteUuidFromUrl;

    const { onBackToEntity } = useBackToEntitySelection();

    const [companySlug] = window.location.host.split('.');

    const renderSubtext = useMemo(() => {
        if (!isLoggedIn)
            return {
                disclosures: '',
                messaging: '',
                surveys: '',
            };

        if (isGuest)
            return {
                disclosures: `${disclosuresCount} ${t('AVAILABLE')}`,
                messaging: '',
                surveys: `${surveysCount} ${t('WAITING')}`,
            };

        return {
            disclosures: `${respondedDisclosures} ${t('SUBMITTED')}`,
            messaging: `${ongoingCases} ${t('ONGOING')}`,
            surveys: `${surveysCount} ${t('WAITING')}`,
        };
    }, [disclosuresCount, isGuest, isLoggedIn, ongoingCases, surveysCount]);

    return (
        <MobileTilesWrapper>
            <TilesSection>
                <TilesHeader>
                    <TilesHeaderAction>
                        {!isLoggedIn && (
                            <TextButton
                                iconProps={{
                                    icon: 'SvgChevronLeft24',
                                    size: 24,
                                    themeColors: {
                                        default: {
                                            'color-1': 'tertiary.default',
                                        },
                                        hover: {
                                            'color-1': 'tertiary.hover',
                                        },
                                    },
                                }}
                                onClick={() => onBackToEntity(companySlug)}
                                testId="fp6-rYk"
                            >
                                {t('BACK_TO_ENTITY_SELECTION')}
                            </TextButton>
                        )}
                        <Typography.TextBody_18_30 fontWeight="normal">
                            {t('FIND_MORE_ABOUT_US')}
                        </Typography.TextBody_18_30>
                    </TilesHeaderAction>

                    <NicoleWrapper>
                        <img
                            alt="nicole"
                            src={MobileDashboardNicole as string}
                        />
                    </NicoleWrapper>
                </TilesHeader>
                <TilesContainer>
                    <MobileMicroserviceBox
                        iconProps={{
                            icon: 'SvgCompanyMessage',
                            size: 38,
                        }}
                        onClick={() =>
                            ModalsProvider.open('MobileCompanyInformationModal')
                        }
                        testId="vhmY9VT"
                        title={t('READ_COMPANY_MESSAGE')}
                    />
                    <MobileMicroserviceBox
                        iconProps={{
                            icon: 'SvgCompanyPolicies',
                            size: 38,
                        }}
                        onClick={() =>
                            ModalsProvider.open('MobileCompanyPoliciesModal')
                        }
                        testId="QqTqN4E"
                        title={t('LEARN_COMPANY_POLICIES')}
                    />
                </TilesContainer>
            </TilesSection>
            <TilesSection>
                <TilesHeader>
                    <Typography.TextBody_18_30 fontWeight="normal">
                        {t(
                            isLoggedIn
                                ? 'WHAT_CAN_WE_DO_FOR_YOU'
                                : 'HOW_CAN_WE_HELP_YOU_TODAY',
                        )}
                    </Typography.TextBody_18_30>
                </TilesHeader>
                <TilesContainer>
                    {modules.cases && (
                        <MobileMicroserviceBox
                            iconProps={{
                                icon: 'SvgMessaging',
                                size: 38,
                            }}
                            onClick={() =>
                                isLoggedIn
                                    ? navigate(
                                          getPath({
                                              path: ongoingCases
                                                  ? '/incidents/reports'
                                                  : '/new-conversation',
                                          }),
                                      )
                                    : ModalsProvider.open(
                                          'MobileMessagingModal',
                                      )
                            }
                            subtext={renderSubtext.messaging}
                            testId="ytjcdVy"
                            title={t('MESSAGING_ACTION_TITLE')}
                        />
                    )}

                    {!isSubsidiaryChosen && modules.disclosures && (
                        <MobileMicroserviceBox
                            iconProps={{
                                icon: 'SvgDAndD',
                                size: 38,
                            }}
                            onClick={() =>
                                isLoggedIn
                                    ? navigate(
                                          getPath({
                                              path: '/disclosure/list',
                                          }),
                                      )
                                    : ModalsProvider.open(
                                          'MobileDisclosuresModal',
                                      )
                            }
                            subtext={renderSubtext.disclosures}
                            testId="oaBgwwm"
                            title={t('DISCLOSURES_ACTION_TITLE')}
                        />
                    )}
                    {!isSubsidiaryChosen && modules.surveys && (
                        <MobileMicroserviceBox
                            iconProps={{
                                icon: 'SvgSurvey',
                                size: 38,
                            }}
                            onClick={() =>
                                isLoggedIn
                                    ? navigate(
                                          getPath({ path: '/survey/list' }),
                                      )
                                    : ModalsProvider.open('MobileSurveysModal')
                            }
                            subtext={renderSubtext.surveys}
                            testId="6l8hY7-"
                            title={t('PARTICIPATE_IN_SURVEY')}
                        />
                    )}
                </TilesContainer>
            </TilesSection>
            {isSubsidiaryChosen && isLoggedIn && (
                <TilesSection>
                    <SwitchToEntityMobileText>
                        {t('DISCLOSURE_MICROSERVICE_BOX_TEXT')}{' '}
                        <SwitchToEntity
                            onClick={() =>
                                ModalsProvider.open('EntityChangeModal')
                            }
                        >
                            {t('SWITCH').toLowerCase()}
                        </SwitchToEntity>{' '}
                        {t('TO_HEADQUARTERS')}
                    </SwitchToEntityMobileText>
                </TilesSection>
            )}
        </MobileTilesWrapper>
    );
};
